import Link from "next/link";
import { ChevronRight } from "lucide-react";
import Reveal from "@/components/ui/Reveal";
import EnterpriseWorkflow, { workflowSteps } from "./EnterpriseWorkflow";

/**
 * The answer to TheProblem: learning that adapts to the person, the role and the goal. Its
 * Assess → Measure strip is the five-step workflow in one line, each word linking to its
 * step on /how-it-works, and the journey diagram follows inside the same frame.
 */
export default function InaraXDifference() {
  return (
    // relative z-10: the Hero stays pinned (position: fixed, z-0) until its bottom leaves
    // the viewport, and on tall screens the cover panel is shorter than the Hero, so this
    // section can scroll in while the Hero is still pinned. Unpositioned, it would paint
    // underneath and the Hero would cover its top edge.
    <section
      id="difference"
      aria-labelledby="difference-heading"
      className="scroll-mt-20 relative z-10 bg-white py-size-xl px-margin-mobile md:px-margin-desktop"
    >
      <div className="max-w-[1440px] mx-auto">
        <Reveal className="max-w-4xl">
          <div className="flex items-center gap-base mb-size-sm">
            <span className="w-12 h-[1px] bg-mkt-primary" />
            <span className="font-label-md text-label-md text-mkt-primary uppercase tracking-widest">
              The inaraX difference
            </span>
          </div>
          <h2
            id="difference-heading"
            className="font-display-lg text-[clamp(30px,3.4vw,48px)] leading-[1.15] tracking-[-0.02em] font-bold text-mkt-inarax-black mb-size-md"
          >
            The best learning is deeply personal.
          </h2>
          <p className="font-body-lg text-body-lg text-mkt-on-surface-variant">
            inaraX adapts learning to the person, the role, and the goal, using AI to personalize every learning path
            and human experts to validate what matters.
          </p>
        </Reveal>

        <Reveal delayMs={100} className="mt-size-lg">
          {/* One row with › arrows from 850px, where all five fit; below that the row would
              wrap, so it becomes a centred vertical column of equal-width pills joined by ↓ arrows. */}
          <ol className="list-none pl-0 ml-0 flex flex-col items-center gap-1 min-[850px]:flex-row min-[850px]:items-center min-[850px]:gap-x-size-xs">
            {workflowSteps.map(({ id, verb }, index) => (
              // The arrow lives inside the <li>: an <ol> may only hold list items.
              <li
                key={id}
                className="flex w-44 flex-col items-center gap-1 min-[850px]:w-auto min-[850px]:flex-row min-[850px]:gap-x-size-xs"
              >
                {index > 0 ? (
                  <ChevronRight
                    aria-hidden="true"
                    className="h-5 w-5 shrink-0 rotate-90 text-mkt-primary/60 min-[850px]:rotate-0"
                  />
                ) : null}
                <Link
                  href={`/how-it-works#${id}`}
                  className="flex w-full items-center justify-center rounded-full border border-mkt-primary/30 bg-mkt-primary-container/10 px-size-md py-2 font-headline-md text-body-md sm:text-body-lg font-semibold text-mkt-primary transition-colors hover:bg-mkt-primary-container hover:text-mkt-on-primary min-[850px]:w-auto"
                >
                  {verb}
                </Link>
              </li>
            ))}
          </ol>
          <p className="mt-size-md font-headline-md text-headline-sm text-mkt-inarax-black">
            Personalized for every learner. <span className="text-mkt-primary-container">Scalable for every organization.</span>
          </p>
        </Reveal>

        <EnterpriseWorkflow />
      </div>
    </section>
  );
}
